import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export function Tabs({ className, children, ...props }) {
  return (
    <div className={cn('flex flex-col gap-4', className)} {...props}>
      {children}
    </div>
  );
}

export function TabsList({ items, value, onChange, className }) {
  const containerRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const node = containerRef.current.querySelector(`[data-tab-key="${value}"]`);
    if (node) {
      const rect = node.getBoundingClientRect();
      const parentRect = containerRef.current.getBoundingClientRect();
      setIndicator({
        left: rect.left - parentRect.left,
        width: rect.width,
        opacity: 1,
      });
    }
  }, [value, items]);

  useEffect(() => {
    function handleResize() {
      const node = containerRef.current?.querySelector(`[data-tab-key="${value}"]`);
      if (node && containerRef.current) {
        const rect = node.getBoundingClientRect();
        const parentRect = containerRef.current.getBoundingClientRect();
        setIndicator({
          left: rect.left - parentRect.left,
          width: rect.width,
          opacity: 1,
        });
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [value]);

  return (
    <div
      className={cn(
        'relative w-full overflow-x-auto',
        'border-b',
        className
      )}
      style={{ borderColor: 'var(--dash-border)' }}
    >
      <div
        ref={containerRef}
        className="relative flex min-w-fit items-center gap-1 px-1"
      >
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.key === value;
          return (
            <button
              key={item.key}
              data-tab-key={item.key}
              type="button"
              onClick={() => onChange?.(item.key)}
              className={cn(
                'group relative flex shrink-0 items-center gap-2 rounded-t-lg px-4 py-3 text-sm font-medium transition-all duration-200'
              )}
              style={{
                color: active ? 'var(--dash-text)' : 'var(--dash-text-muted)',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.color = 'var(--dash-text-soft)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.color = 'var(--dash-text-muted)';
              }}
            >
              {Icon && (
                <Icon
                  size={15}
                  strokeWidth={2.2}
                  className="transition-transform duration-200 group-hover:scale-110"
                  style={{
                    color: active ? 'var(--dash-primary)' : 'currentColor',
                  }}
                />
              )}
              <span className="whitespace-nowrap">{item.label}</span>
              {item.badge && (
                <span
                  className="ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold tabular-nums"
                  style={{
                    background: active
                      ? 'var(--dash-primary)'
                      : 'var(--dash-surface-hover)',
                    color: active ? '#fff' : 'var(--dash-text-soft)',
                  }}
                >
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
        <div
          className="pointer-events-none absolute bottom-0 h-[2px] rounded-full transition-all duration-300 ease-out"
          style={{
            left: indicator.left,
            width: indicator.width,
            opacity: indicator.opacity,
            background: 'var(--dash-primary)',
          }}
        />
      </div>
    </div>
  );
}

export function TabsContent({ className, children, ...props }) {
  return (
    <div className={cn('animate-tab-fade-in', className)} {...props}>
      {children}
      <style>{`
        @keyframes tab-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-tab-fade-in { animation: tab-fade-in 0.25s ease-out; }
      `}</style>
    </div>
  );
}
